import 'slick-carousel/slick/slick.css';
import React, {
  CSSProperties,
  ReactNode,
  useId,
  useState,
  useMemo,
  JSX,
} from 'react';
import SlickSlider from 'react-slick';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition, SizeProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { getProjectType, ProjectTypes } from '@r-and-a-shared-ui/utils';
import classNames from 'classnames';
import ContentListItem from '../ContentListItem';
import { ListDisplay } from '../ContentList.types';
import { ContentListProps } from '../ContentList';
import SliderCustomDots from './SliderCustomDots';
import { useAutoplay } from './useAutoPlay';
import { useScreenDetector } from '../../../hooks';

import stylesPP from './scss/SliderPP.module.scss';
import stylesMMB from './scss/SliderMMB.module.scss';

const RSlick = SlickSlider as unknown as React.ComponentType<any>;

export enum SliderWidth {
  FULL_WIDTH = 'full_width',
}

export interface SliderConfig {
  autoPlay?: boolean;
  autoplayTimeout?: number | null;
  autoplayTimer?: number | null;
  infiniteLoop?: boolean;
  sliderArrowsColor?: string;
  sliderArrowsSize?: string;
  slidesToShowDesktop?: number | null;
  slidesToShowMobile?: number | null;
  slidesToScrollDesktop?: number | null;
  slidesToScrollMobile?: number | null;
  sliderWidth?: string | SliderWidth;
  enableArrows?: boolean;
  appendDots?: (dots: ReactNode) => JSX.Element;
  customNavBar?: (index: number) => JSX.Element | null;
  extraNavChild?: (index: number) => JSX.Element | null;
  isArrowBackgroundTransparent?: boolean;
}

type SliderProps = Omit<ContentListProps, 'type' | 'carouselConfig'> &
  SliderConfig;

interface ArrowProps {
  currentSlide?: number;
  slideCount?: number;
  icon: IconDefinition;
  onClick?: (item: any) => void;
  [key: string]: any;
}

export function Slider(props: SliderProps) {
  const {
    elements,
    autoPlay = false,
    autoplayTimeout,
    autoplayTimer = 5,
    infiniteLoop = false,
    sliderArrowsColor,
    sliderArrowsSize,
    slidesToShowDesktop,
    slidesToShowMobile,
    listDisplay,
    backgroundColor,
    customNavBar,
    appendDots,
    extraNavChild,
    slidesToScrollDesktop,
    slidesToScrollMobile,
    enableArrows,
    sliderWidth,
    isArrowBackgroundTransparent,
  } = props;

  const key = useId();
  const { isAutoPlay } = useAutoplay(autoPlay, autoplayTimer, autoplayTimeout);
  const [activeIndex, setActiveSlide] = useState(0);
  const { isDesktop } = useScreenDetector();
  const nrOfSlides = elements?.length || 0;
  const slidesToShow = useMemo(() => {
    const defaultValue = isDesktop
      ? slidesToShowDesktop || 1
      : slidesToShowMobile || 1;
    return Math.min(defaultValue, nrOfSlides);
  }, [isDesktop, slidesToShowDesktop, slidesToShowMobile, nrOfSlides]);

  const slidesToScroll = useMemo(() => {
    const defaultValue = isDesktop
      ? slidesToScrollDesktop || 1
      : slidesToScrollMobile || 1;
    return Math.min(defaultValue, nrOfSlides);
  }, [isDesktop, slidesToScrollDesktop, slidesToScrollMobile, nrOfSlides]);
  const isNavVisibleByItems = elements ? nrOfSlides > slidesToShow : false;
  const showCustomDots = listDisplay === ListDisplay.HERO_LIST;
  const autoplaySpeedValue = autoplayTimer ? autoplayTimer * 1000 : undefined;
  const isAutoplayValue = isNavVisibleByItems ? isAutoPlay : false;

  const projectType = getProjectType();
  const isPPproject = projectType === ProjectTypes.PlayerPortals;

  const styles = isPPproject ? stylesPP : stylesMMB;

  const arrowBtnProp = {
    color:
      sliderArrowsColor || (isPPproject ? 'var(--color-pp-primary)' : 'white'),
    size: (sliderArrowsSize as SizeProp) || 'lg',
  };

  const SlickArrow = ({
    icon,
    currentSlide,
    slideCount,
    onClick,
    ...props
  }: ArrowProps) =>
    !showCustomDots && enableArrows ? (
      <div
        className={classNames(styles['slider-arrow'], {
          [styles['slider-arrow--transparent']]: isArrowBackgroundTransparent,
        })}
        onClick={onClick}
      >
        <FontAwesomeIcon icon={icon} {...arrowBtnProp} {...props} />
      </div>
    ) : null;

  const customDots = (dots: ReactNode) =>
    appendDots ? (
      appendDots(dots)
    ) : (
      <SliderCustomDots
        extraNavChild={extraNavChild}
        dots={dots}
        customNavBar={customNavBar}
        activeIndex={activeIndex}
      />
    );

  const inlineStyle: CSSProperties = { backgroundColor };

  const responsiveConfig = [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: Math.min(slidesToShowMobile || 1, nrOfSlides),
        slidesToScroll: Math.min(slidesToScrollMobile || 1, nrOfSlides),
        initialSlide: 0,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: Math.min(slidesToShowMobile || 1, nrOfSlides),
        slidesToScroll: Math.min(slidesToScrollMobile || 1, nrOfSlides),
        initialSlide: 0,
      },
    },
  ];

  return (
    <div
      className={classNames(styles.wrapper, listDisplay && styles[listDisplay])}
    >
      <div
        className={classNames(styles['container'], {
          [styles['is-full-width']]: sliderWidth === SliderWidth.FULL_WIDTH,
        })}
        style={inlineStyle}
      >
        <RSlick
          infinite={infiniteLoop}
          arrows={enableArrows}
          prevArrow={<SlickArrow icon={faChevronLeft} />}
          nextArrow={<SlickArrow icon={faChevronRight} />}
          draggable={isNavVisibleByItems && !showCustomDots}
          autoplaySpeed={autoplaySpeedValue}
          autoplay={isAutoplayValue}
          className={classNames({ [styles['slick-slider']]: !showCustomDots })}
          appendDots={customDots}
          dots={showCustomDots}
          beforeChange={(_: any, nextIndex: React.SetStateAction<number>) =>
            setActiveSlide(nextIndex)
          }
          slidesToShow={slidesToShow}
          slidesToScroll={slidesToScrollDesktop || 1}
          responsive={responsiveConfig}
        >
          {
            elements?.map((element, index) => (
              <ContentListItem
                element={element}
                key={element?.id ?? `${index}`}
                listDisplay={listDisplay}
              />
            )) as any
          }
        </RSlick>
      </div>
    </div>
  );
}

export default Slider;
